import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { InvestmentDTO } from '@inversor-hub/types';
import {
  useInviteInvestmentInvestor,
  useResendInvestmentInvestorInvitation,
} from '@/lib/api/investments';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string; message?: string } | undefined;
    return data?.error ?? data?.message ?? 'No se pudo enviar la invitación. Inténtalo de nuevo.';
  }
  return 'No se pudo enviar la invitación. Inténtalo de nuevo.';
};

interface CollaboratorSectionProps {
  investment: InvestmentDTO;
  viewerRole: 'investor' | 'psi';
}

export const CollaboratorSection = ({ investment, viewerRole }: CollaboratorSectionProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const inviteInvestor = useInviteInvestmentInvestor();
  const resendInvitation = useResendInvestmentInvestorInvitation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const isPsiViewer = viewerRole === 'psi';
  const participant = isPsiViewer ? investment.investor : investment.psi;
  const invitationEmail = investment.investorInvitation?.email;
  const title = isPsiViewer ? 'Inversor' : 'PSI';
  const assignedLabel = isPsiViewer ? 'Inversor asignado' : 'PSI asignado';

  const handleClose = () => {
    if (inviteInvestor.isPending) return;
    setDialogOpen(false);
    setEmail('');
    setEmailError(null);
  };

  const handleSubmit = async () => {
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError('Introduce un email válido');
      return;
    }

    try {
      await inviteInvestor.mutateAsync({ investmentId: investment.id, data: { email: trimmed } });
      enqueueSnackbar('Invitación enviada', { variant: 'success' });
      setDialogOpen(false);
      setEmail('');
      setEmailError(null);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  };

  const handleResend = async () => {
    try {
      await resendInvitation.mutateAsync(investment.id);
      enqueueSnackbar('Invitación reenviada', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {participant ? (
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar src={participant.photoUrl} sx={{ width: 40, height: 40 }}>
            {participant.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {participant.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {assignedLabel}
            </Typography>
          </Box>
        </Box>
      ) : isPsiViewer && invitationEmail ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <MarkEmailReadOutlinedIcon color="success" />
            <Box>
              <Typography variant="body1" fontWeight={500}>
                Invitación enviada a {invitationEmail}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pendiente de que el inversor la acepte
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="outlined"
            startIcon={<MarkEmailReadOutlinedIcon />}
            onClick={handleResend}
            disabled={resendInvitation.isPending}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {resendInvitation.isPending ? 'Reenviando...' : 'Reenviar invitación'}
          </Button>
        </Stack>
      ) : isPsiViewer ? (
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography variant="body1" fontWeight={500}>
              Sin inversor asignado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Invita al inversor para que acceda a esta operación. Podrá acceder a la documentación
              y seguir el itinerario.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PersonAddAltIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Invitar inversor
          </Button>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Esta inversión no tiene un PSI asignado.
        </Typography>
      )}

      <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>Invitar inversor</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enviaremos un email con un enlace para que el inversor acepte la invitación y acceda a
            esta operación.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Email del inversor"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (emailError) setEmailError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleSubmit();
              }
            }}
            error={!!emailError}
            helperText={emailError}
            disabled={inviteInvestor.isPending}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={inviteInvestor.isPending}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={inviteInvestor.isPending}>
            {inviteInvestor.isPending ? 'Enviando...' : 'Enviar invitación'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
